<script setup lang="ts">
import { computed, ref, nextTick, toRef } from "vue";
import { useTranslation } from "i18next-vue";
import DRDialog from "@/components/UI/feedbacks/DRDialog.vue";
import PhosphorIcon from "@/components/UI/PhosphorIcon.vue";
import MapIcon from "@/components/UI/icons/MapIcon.vue";
import DRBtn from "@/components/UI/DRBtn.vue";
import { useUpdateDrawing } from "@/stores/queries";
import {
  useGetDiagnostics,
  useCreateDiagnostic,
} from "@/stores/queries/diagnostics.query";
import type { PaginationParams } from "@/types";

const props = defineProps<{
  modelValue: boolean;
  drawingId: number;
  projectId: number;
  /** Nom du plan — sert de nom par défaut à l'ouvrage créé. */
  drawingName?: string;
  /** Ouvrage actuellement lié au plan (pour cocher/délier). */
  diagnostic?: { id: number; name: string | null } | null;
  /** Habillage sombre de l'outil dessin (le canvas est sombre en permanence). */
  canvasTheme?: boolean;
}>();

const emit = defineEmits<{
  (e: "update:modelValue", value: boolean): void;
  /** Émis après liaison/déliaison réussie. */
  (e: "linked", diagnosticId: number | null): void;
}>();

const { t } = useTranslation();

const show = computed({
  get: () => props.modelValue,
  set: (value: boolean) => emit("update:modelValue", value),
});

const drawingId = toRef(props, "drawingId");
const projectId = toRef(props, "projectId");
const { mutateAsync, isPending } = useUpdateDrawing(drawingId);
const { mutateAsync: createDiagnostic, isPending: isCreating } =
  useCreateDiagnostic(projectId);

const pagination = ref<PaginationParams>({
  skip: 0,
  take: 100,
  sortBy: "name",
  order: "asc",
});
const { data: diagnostics, isLoading } = useGetDiagnostics(
  pagination,
  projectId
);

const linkedId = computed(() => props.diagnostic?.id ?? null);

const linkTo = async (diagnosticId: number | null) => {
  await mutateAsync(
    { diagnosticId },
    {
      onSuccess: () => {
        show.value = false;
        emit("linked", diagnosticId);
      },
    }
  );
};

const onSelect = async (diagnosticId: number | null) => {
  if (diagnosticId === linkedId.value) {
    show.value = false;
    return;
  }
  await linkTo(diagnosticId);
};

// ── Création d'un ouvrage à la volée ────────────────────────────────────────
// Parcours depuis l'outil dessin : l'inspecteur n'a qu'un plan et ne veut pas
// aller créer l'ouvrage ailleurs. Le nom du plan sert de proposition.

const isNaming = ref(false);
const newName = ref("");
const nameInput = ref<HTMLInputElement | null>(null);

const startCreate = async () => {
  newName.value = props.drawingName ?? "";
  isNaming.value = true;
  await nextTick();
  nameInput.value?.focus();
  nameInput.value?.select();
};

const cancelCreate = () => {
  isNaming.value = false;
  newName.value = "";
};

/** Crée l'ouvrage puis y rattache le plan dans la foulée. */
const confirmCreate = async () => {
  if (isCreating.value || isPending.value) return;
  const created = await createDiagnostic(newName.value);
  isNaming.value = false;
  await linkTo(created.id);
};
</script>

<template>
  <DRDialog
    v-model="show"
    :class="{ 'canvas-dialog': canvasTheme }"
    :title="t('dashboard:drawings.linkDiagnostic.title')"
    :loading="isPending || isCreating"
    hide-footer
  >
    <div>
      <div class="mb-3 text-grey">
        {{ t("dashboard:drawings.linkDiagnostic.description") }}
      </div>

      <div v-if="isLoading" class="text-center py-4">
        <div class="spinner-border spinner-border-sm" role="status" />
      </div>

      <template v-else>
        <div v-if="!diagnostics?.length" class="text-center text-grey py-3">
          {{ t("dashboard:drawings.linkDiagnostic.empty") }}
        </div>

        <ul v-else class="diagnostic-list">
          <li
            v-for="diag in diagnostics"
            :key="diag.id"
            class="diagnostic-card canvas-dialog-card"
            :class="{ linked: diag.id === linkedId }"
            role="button"
            @click="onSelect(diag.id)"
          >
            <MapIcon :size="32" class="flex-shrink-0" />
            <span class="card-text">
              <span class="card-title">
                {{ diag.name || t("report:diagnostics.item.noname") }}
              </span>
            </span>
            <PhosphorIcon
              v-if="diag.id === linkedId"
              name="check-circle"
              weight="fill"
              :size="20"
              class="text-primary flex-shrink-0"
            />
          </li>
        </ul>

        <div class="or-separator canvas-dialog-separator">
          <span>{{ t("or") }}</span>
        </div>

        <!-- Créer un ouvrage sans quitter l'outil dessin -->
        <div
          v-if="!isNaming"
          class="diagnostic-card create-card canvas-dialog-card"
          role="button"
          @click="startCreate"
        >
          <span class="create-icon canvas-dialog-chip">
            <PhosphorIcon name="plus" :size="18" weight="bold" />
          </span>
          <span class="card-text">
            <span class="card-title">
              {{ t("dashboard:drawings.linkDiagnostic.create") }}
            </span>
            <span class="card-meta canvas-dialog-meta">
              {{ t("dashboard:drawings.linkDiagnostic.createHint") }}
            </span>
          </span>
        </div>

        <div v-else class="create-form">
          <label class="form-label mb-0 text-grey small">
            {{ t("dashboard:drawings.linkDiagnostic.createNameLabel") }}
          </label>
          <input
            ref="nameInput"
            v-model="newName"
            class="form-control form-control-sm"
            :placeholder="
              t('dashboard:drawings.linkDiagnostic.createNamePlaceholder')
            "
            @keydown.enter.prevent="confirmCreate"
            @keydown.esc.prevent="cancelCreate"
          />
          <div class="d-flex justify-content-end gap-2">
            <DRBtn
              :label="t('cancel')"
              variant="flat"
              size="sm"
              @click="cancelCreate"
            />
            <DRBtn
              icon="bi-check-lg"
              :label="t('dashboard:drawings.linkDiagnostic.createConfirm')"
              variant="primary"
              size="sm"
              :loading="isCreating || isPending"
              @click="confirmCreate"
            />
          </div>
        </div>
      </template>

      <div v-if="linkedId" class="mt-3 text-end">
        <button
          class="btn btn-link btn-sm text-danger p-0"
          @click="onSelect(null)"
        >
          {{ t("dashboard:drawings.linkDiagnostic.unlink") }}
        </button>
      </div>
    </div>
  </DRDialog>
</template>

<style lang="scss" scoped>
.diagnostic-list {
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 8px;
  max-height: 320px;
  overflow-y: auto;
}

.diagnostic-card {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 10px 12px;
  border: 1px solid var(--bs-border-color);
  border-radius: 12px;
  background-color: var(--bs-body-bg);
  cursor: pointer;
  transition: border-color 0.15s ease, background-color 0.15s ease;

  &:hover {
    border-color: var(--bs-primary);
    background-color: var(--bs-tertiary-bg);
  }

  &.linked {
    border-color: var(--bs-primary);
    background-color: rgba(var(--bs-primary-rgb), 0.06);
  }
}

.card-text {
  display: flex;
  flex-direction: column;
  min-width: 0;
  flex-grow: 1;
  line-height: 1.3;
}

.card-title {
  font-weight: 500;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.card-meta {
  font-size: 12px;
  color: var(--bs-secondary-color);
}

/* Trait horizontal coupé par un « ou » centré */
.or-separator {
  display: flex;
  align-items: center;
  gap: 12px;
  margin: 16px 0;
  color: var(--bs-secondary-color);
  font-size: 12px;
  text-transform: uppercase;
  letter-spacing: 0.06em;

  &::before,
  &::after {
    content: "";
    flex: 1;
    height: 1px;
    background-color: var(--bs-border-color);
  }
}

.create-card {
  border-style: dashed;
}

.create-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border-radius: 8px;
  background-color: var(--bs-tertiary-bg);
  color: var(--bs-primary);
  flex-shrink: 0;
}

.create-form {
  display: flex;
  flex-direction: column;
  gap: 8px;
}
</style>
